import dateFnsFormat from 'date-fns/format';
import React from 'react';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { LocaleConfigContext } from '../../../contexts/locale-config/locale-config';
import { __ } from '../../../utils/translation-service';
import { DateTypes } from './calendar';
import { dateTypeEventMap } from '../cruise-search-mask/cruise-search-mask';
import {
    StyledCalendarWrapper,
    StyledEndDateWrapper,
    StyledStartDateWrapper,
    StyledXIcon,
} from './calendar.style';
import { getDateFormatByLocale, MONTHS, WEEKDAYS_SHORT, FIRST_DAY_OF_WEEK } from './localization';
import { eventTracker } from '../../../utils/EventTracker';

interface IProps {
    startDate?: Date;
    endDate?: Date;
    onDateChange?: ({ startDate, endDate }: { startDate?: Date; endDate?: Date }) => void;
    hideSeparator?: boolean;
    focusedInput?: DateTypes;
    isMobile?: boolean;
    focusId?: boolean;
}

const Calendar = (props: IProps) => {
    const [startDate, setStartDate] = React.useState<Date | undefined>(props.startDate);
    const [endDate, setEndDate] = React.useState<Date | undefined>(props.endDate);
    const [focusedInput, setFocusedInput] = React.useState<DateTypes>(props.focusedInput);

    const localeConfig = React.useContext(LocaleConfigContext);

    const endInputRef = React.useRef<HTMLInputElement>(null);
    const startInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!props.startDate && !props.endDate) {
            // Reset via props (used on mobile)
            setStartDate(undefined);
            setEndDate(undefined);
        }
    }, [props.startDate, props.endDate]);

    //Using the local state focusInput for animation.
    React.useEffect(() => focus(props.focusedInput), [props.focusedInput]);

    const modifiers = { start: startDate, end: endDate };

    React.useEffect(() => {
        props.onDateChange?.({ startDate, endDate });
    }, [props.onDateChange, startDate, endDate]);

    const changeDate = (date: Date | undefined, type: DateTypes) => {
        if (type) {
            eventTracker.ga4Track('search_filters', {
                page_type: 'Ship Landing Page',
                event_type: dateTypeEventMap[type],
            });
        }
        
        if (type === 'startDate') setStartDate(date);
        if (type === 'endDate') setEndDate(date);
    };

    // Format the date in the input field.
    const formatDate = (date: Date | undefined) => {
        return date ? dateFnsFormat(date, getDateFormatByLocale(localeConfig.code)) : '';
    };

    const focus = (type: DateTypes) => {
        setFocusedInput(type);
        if (type === 'startDate') {
            startInputRef?.current?.focus();
        } else {
            endInputRef?.current?.focus();
        }
    };
    const flipFocus = () => {
        if (focusedInput === 'startDate') {
            endInputRef?.current?.focus();
        } else {
            startInputRef?.current?.focus();
        }
    };

    const resetDate = (type: DateTypes) => {
        focus(type);
        if (type === 'startDate') setStartDate(undefined);
        if (type === 'endDate') setEndDate(undefined);
    };

    const locale = localeConfig.code ?? 'en';
    const months = MONTHS[locale]?.length ? MONTHS[locale] : MONTHS['us'];
    const weekdaysShort = WEEKDAYS_SHORT[locale]?.length
        ? WEEKDAYS_SHORT[locale]
        : WEEKDAYS_SHORT['us'];
    const firstDayOfWeek = FIRST_DAY_OF_WEEK[locale]
        ? FIRST_DAY_OF_WEEK[locale]
        : FIRST_DAY_OF_WEEK['us'];

    const focusId = props.focusId ?? true;
    return (
        <StyledCalendarWrapper isModal={true} open={focusId ? focusedInput : null}>
            <div>
                <StyledStartDateWrapper>
                    {startDate && <StyledXIcon onClick={() => resetDate('startDate')} />}
                    <input
                        ref={startInputRef}
                        onFocus={() => setFocusedInput('startDate')}
                        value={formatDate(startDate)}
                        placeholder={__('startDatePlaceholder')}
                        readOnly={true}
                    />
                </StyledStartDateWrapper>
                <StyledEndDateWrapper hideSeparator={props.hideSeparator}>
                    {endDate && <StyledXIcon onClick={() => resetDate('endDate')} />}
                    <input
                        ref={endInputRef}
                        onFocus={() => setFocusedInput('endDate')}
                        value={formatDate(endDate)}
                        placeholder={__('endDatePlaceholder')}
                        readOnly={true}
                    />
                </StyledEndDateWrapper>
            </div>
            <DayPicker
                selectedDays={[startDate, { from: startDate, to: endDate }] as any}
                disabledDays={{ before: new Date() }}
                modifiers={modifiers}
                numberOfMonths={24}
                onDayClick={(day) => {
                    changeDate(day, focusedInput);
                    flipFocus();
                }}
                locale={locale}
                months={months}
                weekdaysShort={weekdaysShort}
                pagedNavigation={false}
                firstDayOfWeek={firstDayOfWeek}
            />
        </StyledCalendarWrapper>
    );
};

export default Calendar;
